import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fmt, fmt1, fmtMoney } from "../lib/format";
import { patchEntry, errMsg } from "../lib/api";
import { SourceBadge, StatusBadge } from "./Badges";

const field = "h-8 rounded-sm border border-slate-700 bg-[#182232] px-2 text-xs text-slate-100 outline-none transition-colors focus:border-sky-500";
const ISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dateWarning = (v) => {
  if (!v) return "";
  const today = new Date();
  const max = new Date(today.getTime() + 7 * 86400000);
  return v < ISO(today) || v > ISO(max) ? "Amazon asks for a date between today and the next weekly report" : "";
};

export const ReportRow = ({ r, reasons, onSaved }) => {
  const [qty, setQty] = useState(r.replenishment_quantity ?? "");
  const [comment, setComment] = useState(r.additional_comments);
  useEffect(() => { setQty(r.replenishment_quantity ?? ""); setComment(r.additional_comments); }, [r.replenishment_quantity, r.additional_comments]);
  const low = r.status === "low";
  const warn = low ? dateWarning(r.next_replenishment_date) : "";
  const need = (v) => (low && (v === "" || v === null || v === undefined) ? "border-red-500/60" : "");
  const id = r.key;
  const save = async (body) => {
    try { await patchEntry(r.key, body); await onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <tr data-testid={`report-row-${id}`} className={low ? "bg-red-500/[0.04]" : ""}>
      <td className="num text-xs">{r.country}</td>
      <td className="num text-xs">{r.site}</td>
      <td className="num text-xs text-slate-200">{r.item}</td>
      <td className="text-xs text-slate-400">{r.friendly_name}</td>
      <td className="num text-right text-xs text-slate-400">{fmt(r.two_week_max)}</td>
      <td className="num text-right text-xs text-slate-400">{fmt(r.three_week_max)}</td>
      <td className="num text-right text-xs text-slate-400">{fmt(r.four_week_max)}</td>
      <td className="num text-right text-xs font-semibold" data-testid={`report-stock-${id}`}>{fmt(r.stock_total_inventory)}</td>
      <td><SourceBadge source={r.source} /></td>
      <td className={`num text-right text-xs ${low ? "text-red-400" : ""}`} data-testid={`report-doh-${id}`}>{fmt(r.doh)}</td>
      <td className="num text-right text-xs">{fmt1(r.weeks_cover)}</td>
      <td className="num text-right text-xs text-sky-300">{fmtMoney(r.stock_value)}</td>
      <td><StatusBadge status={r.status} testId={`report-status-${id}`} /></td>
      <td>
        <select data-testid={`reason-select-${id}`} disabled={!low} value={r.reason_for_low_doh} onChange={(e) => save({ reason_for_low_doh: e.target.value || null })} className={`${field} w-52 disabled:opacity-30 ${low && !r.reason_for_low_doh ? "border-red-500/60" : ""}`}>
          <option value="">{low ? "Select reason…" : "—"}</option>
          {reasons.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td>
        <input data-testid={`replen-date-${id}`} type="date" disabled={!low} value={r.next_replenishment_date} onChange={(e) => save({ next_replenishment_date: e.target.value || null })} title={warn || "Next replenishment date"} className={`${field} w-36 disabled:opacity-30 ${need(r.next_replenishment_date)} ${warn ? "border-amber-500/70" : ""}`} />
      </td>
      <td>
        <input data-testid={`replen-qty-${id}`} type="number" min="0" disabled={!low} value={qty} onChange={(e) => setQty(e.target.value)} onBlur={() => save({ replenishment_quantity: qty === "" ? null : Number(qty) })} className={`${field} num w-28 text-right disabled:opacity-30 ${need(r.replenishment_quantity)}`} />
      </td>
      <td>
        <input data-testid={`comment-${id}`} value={comment} onChange={(e) => setComment(e.target.value)} onBlur={() => comment !== r.additional_comments && save({ additional_comments: comment })} placeholder="Comment" className={`${field} w-44`} />
      </td>
    </tr>
  );
};
